import {
  anyCaptureAvailable,
  deriveCheckers,
  generateCaptureSequences,
  simpleDestinations,
  type CheckersMove,
  type Coord,
} from '@awwyeah/shared';

/**
 * Client-side pre-checks for Checkers (WS-17d) — the `deriveCheckers` replay is the real
 * authority (same CRDT-merge-safety story as Connect-4's `gameOps.ts`'s `canPlayMove`), but
 * an honest client shouldn't emit a move it already knows is illegal.
 */

/** Every complete legal path (full capture sequence, or the single simple destination) the
 * piece at `from` can take right now — what `CheckersBoardPage` offers as tap targets once a
 * piece is selected. Empty if it isn't `userId`'s turn, `from` isn't their piece, or (when a
 * capture is forced elsewhere) this particular piece has no capture of its own. */
export function legalPathsForPiece(
  movesSoFar: readonly CheckersMove[],
  playerIds: readonly [string, string],
  userId: string,
  from: Coord,
): Coord[][] {
  const state = deriveCheckers(movesSoFar, playerIds);
  if (state.turn === null) return [];
  if (playerIds[state.turn] !== userId) return [];
  const piece = state.board[from[0]]?.[from[1]];
  if (!piece || piece.player !== state.turn) return [];

  if (state.forcedCapture)
    return generateCaptureSequences(state.board, from, state.turn, piece.king);
  if (anyCaptureAvailable(state.board, state.turn)) return []; // defensive — shouldn't happen
  return simpleDestinations(state.board, from, piece).map((dest) => [dest]);
}
